import { Controller } from "@hotwired/stimulus"
import videojs from "video.js"
import "video.js/dist/video-js.css"
import "@videojs/http-streaming"
import "videojs-contrib-quality-levels"
import "videojs-hls-quality-selector"

export default class extends Controller {
  static targets = ["modal", "container"]
  static values = {
    fallbackUrl: { type: String }
  }

  connect() {
    this.player = null
    this.playerInitialized = false
  }

  disconnect() {
    this.disposePlayer()
  }

  play(event) {
    const button = event.currentTarget
    const hlsUrl = button.dataset.hlsUrl
    const fallbackUrl = button.dataset.fallbackUrl || this.fallbackUrlValue

    try {
      this.initializePlayer(hlsUrl || fallbackUrl)
    } catch (error) {
      console.error("Error initializing player:", error)
      // Try with fallback URL if HLS URL failed
      if (hlsUrl && fallbackUrl && hlsUrl !== fallbackUrl) {
        console.log("Trying fallback URL:", fallbackUrl)
        this.initializePlayer(fallbackUrl)
      }
    }
  }

  close() {
    this.modalTarget.classList.add("hidden")
    if (this.player) {
      this.player.pause()
    }

    // Completely dispose the player when closing
    this.disposePlayer()

    // Clean up the container
    if (this.containerTarget) {
      this.containerTarget.innerHTML = ''
    }

    // Reset flag
    this.playerInitialized = false
  }

  closeBackground(event) {
    if (event.target === this.modalTarget) {
      this.close()
    }
  }

  closeWithEscape(event) {
    if (event.key === "Escape" && !this.modalTarget.classList.contains("hidden")) {
      this.close()
    }
  }

  initializePlayer(src) {
    if (!src) {
      console.error("No valid source URL provided")
      return
    }

    // If player is already initialized, we should first clean up
    if (this.playerInitialized) {
      this.close()
    }

    // Create fresh video element
    const videoElement = document.createElement("video")
    videoElement.id = "hls-video"
    videoElement.className = "video-js vjs-big-play-centered"
    videoElement.controls = true
    videoElement.preload = "auto"
    videoElement.crossOrigin = "anonymous" // Add CORS support

    // Clean container and add the new video element
    this.containerTarget.innerHTML = ''
    this.containerTarget.appendChild(videoElement)

    // Determine if we're using HLS based on source URL
    const isHls = src.includes(".m3u8")

    // If it's HLS, set up the quality selector in the control bar
    let controlBarItems = [
      "playToggle",
      "volumePanel",
      "currentTimeDisplay",
      "timeDivider",
      "durationDisplay",
      "progressControl",
      "playbackRateMenuButton",
      "fullscreenToggle"
    ]

    // Add quality selector to control bar for HLS videos
    if (isHls) {
      controlBarItems.splice(controlBarItems.length - 1, 0, "qualitySelector")
    }
    // Initialize player
    this.player = videojs("hls-video", {
      fluid: true,
      responsive: true,
      playbackRates: [0.5, 1, 1.5, 2],
      html5: {
        hls: {
          enableLowInitialPlaylist: true,
          bandwidth: 5000000,
          useBandwidthFromLocalStorage: true,
          overrideNative: !videojs.browser.IS_SAFARI, // Use native HLS on Safari
          handleManifestRedirects: true
        },
        nativeTextTracks: false
      },
      controlBar: {
        children: controlBarItems
      },
      plugins: {
        // Initialize HLS quality selector plugin for HLS content
        hlsQualitySelector: isHls ? {
          displayCurrentQuality: true,
          placementIndex: 7, // Position in control bar before fullscreen
          vjsIconClass: 'vjs-icon-hd'
        } : false
      }
    })

    // Mark player as initialized
    this.playerInitialized = true

    // Set source type based on URL
    const sourceType = isHls ? "application/x-mpegURL" : (src.endsWith(".mp4") ? "video/mp4" : "video/webm")

    this.player.src({
      src: src,
      type: sourceType
    })

    // Add error handling
    this.player.on("error", (e) => {
      console.error("Player error:", this.player.error())

      // If using HLS and got an error, try direct video URL if available
      const currentSrc = this.player.currentSrc()
      const fallbackUrl = this.element.querySelector(".webinar-play-btn")?.dataset.fallbackUrl

      if (currentSrc.includes(".m3u8") && fallbackUrl && !fallbackUrl.includes(".m3u8")) {
        console.log("HLS playback failed, trying direct video URL:", fallbackUrl)
        this.player.src({
          src: fallbackUrl,
          type: fallbackUrl.endsWith(".mp4") ? "video/mp4" : "video/webm"
        })
        this.player.play()
      }
    })

    // Monitor HLS quality levels and debug if needed
    if (isHls) {
      this.player.qualityLevels().on('addqualitylevel', (event) => {
        const qualityLevel = event.qualityLevel
        console.log(`Added quality level: ${qualityLevel.width}x${qualityLevel.height} @ ${Math.round(qualityLevel.bitrate / 1000)}kbps`)
      })

      // Add manual quality options if automatic detection fails
      this.player.on("loadedmetadata", () => {
        // Log all quality levels
        try {
          const qualityLevels = this.player.qualityLevels()
          console.log(`Found ${qualityLevels.length} quality levels`)

          // If no quality levels are detected, we'll manually add them
          if (!qualityLevels || qualityLevels.length <= 1) {
            console.log("No multiple quality levels found in automatic detection, adding manual options")

            // Create a custom menu for quality selection
            const qualityContainer = this.player.controlBar.getChild('qualitySelector')
            if (qualityContainer) {
              // Force quality selector to be visible
              const qualityButton = this.player.controlBar.getChild('qualitySelector')
              if (qualityButton) {
                // Make sure it's visible
                qualityButton.show()

                // Access the menu and re-initialize it
                if (qualityButton.menu) {
                  const menu = qualityButton.menu

                  // Make sure menu is showing
                  menu.options_.title = 'Quality'

                  // Manual quality options - these should match your available streams
                  const qualities = [
                    { label: '1080p', value: '1080p' },
                    { label: '720p', value: '720p' },
                    { label: '480p', value: '480p' }
                  ]

                  // Add manual menu items if needed
                  if (menu.children().length <= 1) {
                    console.log("Adding manual quality menu items")
                    qualities.forEach(quality => {
                      menu.addItem(quality.label, () => {
                        console.log(`Manually selected quality: ${quality.label}`)
                        // Switching logic would go here if needed
                      })
                    })
                  }
                }
              }
            }
          }
        } catch (error) {
          console.warn("Error handling quality levels:", error)
        }
      })
    }

    // Auto play when ready
    this.player.ready(() => {
      // Show the modal
      this.modalTarget.classList.remove("hidden")

      // Begin playback
      this.player.play().catch(error => {
        console.log("Playback prevented due to browser restrictions:", error)
      })
    })
  }

  disposePlayer() {
    if (this.player) {
      try {
        this.player.dispose()
      } catch (e) {
        console.warn("Error disposing player:", e)
      }
      this.player = null
    }
  }
}
